import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Button,
    Modal, ModalDialog, ModalClose, Divider, Textarea, Checkbox,
    Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon       from '@mui/icons-material/ArrowBackRounded';
import EmojiEventsRoundedIcon     from '@mui/icons-material/EmojiEventsRounded';
import AddRoundedIcon             from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon          from '@mui/icons-material/DeleteRounded';
import CalendarTodayRoundedIcon   from '@mui/icons-material/CalendarTodayRounded';
import PersonRoundedIcon          from '@mui/icons-material/PersonRounded';
import LocationOnRoundedIcon      from '@mui/icons-material/LocationOnRounded';
import StarRoundedIcon            from '@mui/icons-material/StarRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';

import UndoToast from '../../components/UndoToast';
import DateQuickPicker from '../../components/DateQuickPicker';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetRewards, APICreateReward, APIDeleteReward, APIBulkDeleteRewards,
    APIGetEmployees, APIGetLocations,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import useMultiSelect from '../../hooks/useMultiSelect';
import SelectionToolbar from '../../components/SelectionToolbar';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

// ─── Предустановленные периоды ────────────────────────────────────────────────
const getPeriods = () => {
    const now = new Date();
    const today = toISO(now);

    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return [
        { label: 'Сегодня',  from: today,               to: today },
        { label: 'Вчера',    from: toISO(yesterday),     to: toISO(yesterday) },
        { label: 'Неделя',   from: toISO(weekStart),     to: today },
        { label: 'Месяц',    from: toISO(monthStart),    to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};


// ════════════════════════════════════════════════════════════════════
//  RewardsPage
// ════════════════════════════════════════════════════════════════════
export default function RewardsPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('finance.rewards.create');
    const canDelete = hasPerm('finance.rewards.delete');

    // ── Справочники ───────────────────────────────────────────────
    const [employees,  setEmployees]  = useState([]);
    const [locations,  setLocations]  = useState([]);

    // ── Состояние списка ──────────────────────────────────────────
    const [rewards,      setRewards]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(() => toISO(new Date()));
    const [filterPhId,   setFilterPhId]   = useState('');
    const [activePeriod, setActivePeriod] = useState('Месяц');

    // ── Модалка добавления ────────────────────────────────────────
    const [addOpen,     setAddOpen]     = useState(false);
    const [addPhIds,       setAddPhIds]       = useState([]);
    const [addLocId,    setAddLocId]    = useState('');
    const [addValue,    setAddValue]    = useState('');
    const [addDate,     setAddDate]     = useState(() => toISO(new Date()));
    const [addComment,  setAddComment]  = useState('');
    const [addIsAdmin,  setAddIsAdmin]  = useState(false);
    const [submitting,  setSubmitting]  = useState(false);

    // ── Модалка удаления ──────────────────────────────────────────
    const [deleteId,    setDeleteId]    = useState(null);
    const [deleting,    setDeleting]    = useState(false);
    const [undoReward, setUndoReward] = useState(null);
    
    const [tab, setTab] = useState('all'); // all | manual | auto

    const sel = useMultiSelect();
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const handleBulkDelete = async () => {
        if (sel.count === 0) return;
        if (!window.confirm(`Удалить выбранные премии (${sel.count})? Действие необратимо.`)) return;
        setBulkDeleting(true);
        const res = await APIBulkDeleteRewards(token, sel.ids);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast(`Удалено премий: ${res.deleted ?? sel.ids.length}`);
            sel.exitSelectMode();
            loadRewards();
        }
        setBulkDeleting(false);
    };

    // ── Загрузка справочников ─────────────────────────────────────
    useEffect(() => {
        (async () => {
            const [emplRaw, locs] = await Promise.all([
                APIGetEmployees(token),
                APIGetLocations(token),
            ]);
            const empl = Array.isArray(emplRaw)
                ? emplRaw.filter(e => ['photographer', 'admin', 'admin_photographer'].includes(e.role))
                : [];
            setEmployees(empl);
            if (Array.isArray(locs)) {
                setLocations(locs);
                if (locs.length === 1) setAddLocId(locs[0].id);
            }
        })();
    }, [token]);

    // ── Загрузка премий ───────────────────────────────────────────
    const loadRewards = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom)   params.date_from       = dateFrom;
        if (dateTo)     params.date_to         = dateTo;
        if (filterPhId) params.photographer_id = filterPhId;

        const data = await APIGetRewards(token, params);
        if (Array.isArray(data)) setRewards(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, filterPhId]);

    useEffect(() => { loadRewards(); }, [loadRewards]);
    useEffect(() => onRefreshTick(loadRewards), [onRefreshTick, loadRewards]);


    // ── Итого ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const manual = rewards.filter(r => r.is_manual);
        const auto   = rewards.filter(r => !r.is_manual);
        const manualSum = manual.reduce((s, r) => s + parseFloat(r.value || 0), 0);
        const autoSum   = auto.reduce((s, r) => s + parseFloat(r.value || 0), 0);
        const total     = manualSum + autoSum;
        return { total, manualSum, autoSum, count: rewards.length, manualCount: manual.length, autoCount: auto.length };
    }, [rewards]);

    // ── Группировка по дате ───────────────────────────────────────
    const filteredRewards = useMemo(() => {
        if (tab === 'manual') return rewards.filter(r =>  r.is_manual);
        if (tab === 'auto')   return rewards.filter(r => !r.is_manual);
        return rewards;
    }, [rewards, tab]);

    const grouped = useMemo(() => {
        const map = {};
        const sorted = [...filteredRewards].sort((a, b) =>
            (b.date || '').localeCompare(a.date || '') || String(b.id).localeCompare(String(a.id)));
        for (const r of sorted) {
            const key = r.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(r);
        }
        return Object.entries(map);
    }, [filteredRewards]);

    // Выбираем все видимые премии (включая авто).
    const selectableIds = useMemo(
        () => filteredRewards.map(r => r.id),
        [filteredRewards],
    );

    // ── Добавление премии ─────────────────────────────────────────
    const handleAdd = async () => {
        if (!addPhIds.length) { showToast('Выберите хотя бы одного сотрудника', 'warning'); return; }
        if (!addLocId) { showToast('Выберите точку', 'warning'); return; }
        if (!addValue || parseFloat(addValue) <= 0) { showToast('Укажите сумму', 'warning'); return; }
        if (!addDate)  { showToast('Укажите дату', 'warning'); return; }

        setSubmitting(true);
        const res = await APICreateReward(token, {
            photographer_ids: addPhIds,
            location_id:      addLocId,
            value:            parseFloat(addValue),
            is_administrator: addIsAdmin,
            date:             addDate,
            comment:          addComment,
        });

        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            // Сервер возвращает массив, если фотографов > 1
            const rewards = Array.isArray(res) ? res : [res];
            const totalSum = rewards.reduce((s, r) => s + parseFloat(r.value), 0);
            const message = rewards.length === 1
                ? `Премия ${fmtMoney(rewards[0].value)} ₽ добавлена`
                : `Премии добавлены: ${rewards.length} шт. на ${fmtMoney(totalSum)} ₽`;
            setUndoReward({ id: rewards[0].id, ids: rewards.map(r => r.id), message });
            setAddOpen(false);
            resetAddForm();
            loadRewards();
        }
        setSubmitting(false);
    };

    const resetAddForm = () => {
        setAddPhIds([]);
        setAddLocId(locations.length === 1 ? locations[0].id : '');
        setAddValue('');
        setAddDate(toISO(new Date()));
        setAddComment('');
        setAddIsAdmin(false);
    };

    // ── Удаление премии ───────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        const res = await APIDeleteReward(token, deleteId);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Премия удалена');
            setDeleteId(null);
            loadRewards();
        }
        setDeleting(false);
    };

    const handleUndoReward = async () => {
        if (!undoReward) return;

        // Если в undo был массив id — отменяем все по очереди
        const ids = undoReward.ids || [undoReward.id];
        let hadError = false;
        for (const id of ids) {
            const res = await APIDeleteReward(token, id);
            if (res?.error) hadError = true;
        }
        if (hadError) {
            showToast('Часть премий не удалось отменить', 'danger');
        } else {
            showToast(ids.length === 1 ? 'Премия отменена' : `Отменено: ${ids.length} премий`);
        }
        loadRewards();
        setUndoReward(null);
    };

    // ── Рендер ────────────────────────────────────────────────────
    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/manager')}
                            sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Премии
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Начисления сотрудникам
                            </Typography>
                        </Box>
                    </Box>
                    {canCreate && (
                        <Button
                            size="sm"
                            startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setAddOpen(true)}
                            sx={{
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                            }}
                        >
                            Добавить
                        </Button>
                    )}
                </Box>

                {/* ── Быстрый выбор периода ────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ mb: 2 }}>
                        <DateQuickPicker
                            activeKey={activePeriod}
                            onChange={(from, to, key) => {
                                setActivePeriod(key);
                                setDateFrom(from);
                                setDateTo(to);
                            }}
                        />
                    </Box>

                    {/* Ручной выбор дат + фильтр по сотруднику */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                        />
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                        />
                    </Box>
                    <Select
                        value={filterPhId}
                        onChange={(_, v) => setFilterPhId(v || '')}
                        placeholder="Все сотрудники"
                        size="sm"
                        sx={{ mb: 2 }}
                        slotProps={{
                            listbox: {
                                sx: {
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                    '& .MuiOption-root': {
                                        color: 'var(--joy-palette-text-primary)',
                                        fontSize: 13,
                                        '&:hover': {
                                            bgcolor: 'rgba(162,38,226,0.12)',
                                            color: 'var(--joy-palette-text-primary)',
                                        },
                                        '&[aria-selected="true"]': {
                                            bgcolor: 'rgba(162,38,226,0.2)',
                                            color: 'var(--joy-palette-primary-300)',
                                            fontWeight: 600,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <Option value="">Все сотрудники</Option>
                        {employees.map(e => (
                            <Option key={e.id} value={e.id}>
                                {e.first_name} {e.last_name}
                            </Option>
                        ))}
                    </Select>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1, mb: 2.5 }}>
                        {/* Общая сумма */}
                        <Box sx={{
                            gridColumn: '1 / -1',
                            px: 2, py: 1.8,
                            borderRadius: 'var(--joy-radius-lg)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'rgba(162,38,226,0.08)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <Box>
                                <Box sx={{ color: 'var(--joy-palette-primary-400)', mb: 0.5 }}>
                                    <EmojiEventsRoundedIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ fontSize: 28, fontWeight: 800, color: 'var(--joy-palette-primary-300)', lineHeight: 1 }}>
                                    {fmtMoney(totals.total)} ₽
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                    Всего · {totals.count} премий
                                </Typography>
                            </Box>
                        </Box>

                        {/* Ручные */}
                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(34,197,94,0.08)' }}>
                            <Box sx={{ color: '#22c55e', mb: 0.3 }}><PersonRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#22c55e', lineHeight: 1 }}>
                                {fmtMoney(totals.manualSum)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Ручные · {totals.manualCount}
                            </Typography>
                        </Box>

                        {/* Автоматические */}
                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(129,140,248,0.08)' }}>
                            <Box sx={{ color: '#818cf8', mb: 0.3 }}><StarRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#818cf8', lineHeight: 1 }}>
                                {fmtMoney(totals.autoSum)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Авто · {totals.autoCount}
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Переключатель: Все / Ручные / Авто ──────────────────── */}
                <Box sx={{
                    display: 'inline-flex',
                    p: 0.4,
                    mb: 2,
                    borderRadius: 'var(--joy-radius-lg)',
                    border: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'var(--joy-palette-background-level1)',
                    gap: 0.3,
                    flexWrap: 'wrap',
                }}>
                    {[
                        {
                            value: 'all',
                            label: 'Все',
                            count: totals.count,
                            color: 'var(--joy-palette-primary-400)',
                            bg: 'rgba(162,38,226,0.10)',
                        },
                        {
                            value: 'manual',
                            label: 'Ручные',
                            count: totals.manualCount,
                            color: '#22c55e',
                            bg: 'rgba(34,197,94,0.10)',
                        },
                        {
                            value: 'auto',
                            label: '⚙ Авто',
                            count: totals.autoCount,
                            color: '#f59e0b',
                            bg: 'rgba(245,158,11,0.10)',
                        },
                    ].map(opt => {
                        const active = tab === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                onClick={() => setTab(opt.value)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.8,
                                    px: 1.8, py: 0.9,
                                    borderRadius: 'var(--joy-radius-md)',
                                    bgcolor: active ? opt.bg : 'transparent',
                                    border: '1px solid',
                                    borderColor: active ? `${opt.color}40` : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all .15s',
                                    '&:hover': {
                                        bgcolor: active ? opt.bg : 'var(--joy-palette-background-level2)',
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: active ? opt.color : 'var(--joy-palette-text-secondary)',
                                    transition: 'color .15s',
                                }}>
                                    {opt.label}
                                </Typography>
                                <Chip
                                    size="sm"
                                    sx={{
                                        bgcolor: active ? `${opt.color}25` : 'var(--joy-palette-background-level2)',
                                        color: active ? opt.color : 'var(--joy-palette-text-tertiary)',
                                        fontWeight: 800,
                                        fontSize: 10,
                                        minHeight: 18,
                                        px: 0.8,
                                        border: 'none',
                                    }}
                                >
                                    {opt.count}
                                </Chip>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Список премий ─────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : rewards.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <EmojiEventsRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет премий за выбранный период
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.5}>

                            {canDelete && selectableIds.length > 0 && (
                                <SelectionToolbar
                                    selectMode={sel.selectMode}
                                    onEnter={() => sel.setSelectMode(true)}
                                    onExit={sel.exitSelectMode}
                                    count={sel.count}
                                    totalSelectable={selectableIds.length}
                                    allChecked={selectableIds.length > 0 && sel.count === selectableIds.length}
                                    onToggleAll={(e) => e.target.checked ? sel.selectAll(selectableIds) : sel.clear()}
                                    onDelete={handleBulkDelete}
                                    deleting={bulkDeleting}
                                    enterLabel="Выбрать несколько"
                                    deleteLabel="Удалить премии"
                                />
                            )}

                            {grouped.map(([date, items]) => {
                                const dayTotal = items.reduce((s, r) => s + parseFloat(r.value || 0), 0);
                                return (
                                    <motion.div key={date} variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            overflow: 'hidden',
                                        }}>
                                            {/* Заголовок дня */}
                                            <Box sx={{
                                                px: 2, py: 1,
                                                borderBottom: '1px solid var(--joy-palette-divider)',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {fmtDate(date)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-primary-300)', fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtMoney(dayTotal)} ₽
                                                </Typography>
                                            </Box>

                                            {/* Строки */}
                                            {items.map((reward, idx) => (
                                                <Box key={reward.id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    px: 2, py: 1.2,
                                                    borderBottom: idx < items.length - 1
                                                        ? '1px solid var(--joy-palette-divider)' : 'none',
                                                }}>
                                                    {sel.selectMode && canDelete && !reward.is_daily && (
                                                        <Checkbox size="sm"
                                                            checked={sel.isSelected(reward.id)}
                                                            onChange={() => sel.toggle(reward.id)}
                                                            sx={{ mr: 0.5, flexShrink: 0 }} />
                                                    )}
                                                    
                                                    {/* Аватар */}
                                                    <Avatar src={reward.photographer_avatar_url} size="sm" sx={{
                                                        width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                        bgcolor: 'rgba(162,38,226,0.15)', color: 'var(--joy-palette-primary-300)',
                                                        border: '1.5px solid var(--joy-palette-primary-500)', flexShrink: 0,
                                                    }}>
                                                        {(reward.photographer_name || '').charAt(0).toUpperCase()}
                                                    </Avatar>

                                                    {/* Инфо */}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                            {reward.photographer_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2 }}>
                                                            {reward.location_name}
                                                            {reward.comment ? ` · ${reward.comment}` : ''}
                                                        </Typography>
                                                        {reward.created_by_name && (
                                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                                Начислил: {reward.created_by_name} · {new Date(reward.date_create).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {/* Тип */}
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        {reward.is_administrator && (
                                                            <Chip size="sm" variant="soft" sx={{
                                                                fontSize: 10, fontWeight: 700,
                                                                bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b',
                                                            }}
                                                                startDecorator={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 12 }} />}
                                                            >
                                                                Админ
                                                            </Chip>
                                                        )}
                                                        <Chip size="sm" variant="soft" sx={{
                                                            fontSize: 10, fontWeight: 700,
                                                            bgcolor: reward.is_daily ? 'rgba(6,182,212,0.10)' : (reward.is_manual ? 'rgba(34,197,94,0.10)' : 'rgba(129,140,248,0.10)'),
                                                            color: reward.is_daily ? '#06b6d4' : (reward.is_manual ? '#22c55e' : '#818cf8'),
                                                        }}>
                                                            {reward.is_daily ? 'За выручку' : (reward.is_manual ? 'Ручная' : 'Авто-расчёт')}
                                                        </Chip>
                                                    </Box>

                                                    {/* Сумма */}
                                                    <Typography sx={{
                                                        fontSize: 15, fontWeight: 800,
                                                        color: '#22c55e',
                                                        minWidth: 55, textAlign: 'right',
                                                        fontVariantNumeric: 'tabular-nums',
                                                    }}>
                                                        +{fmtMoney(reward.value)}
                                                    </Typography>

                                                    {/* Удалить (только ручные) */}
                                                    {reward.is_manual && canDelete && (
                                                        <IconButton
                                                            size="sm" variant="plain"
                                                            onClick={() => setDeleteId(reward.id)}
                                                            sx={{
                                                                color: 'var(--joy-palette-text-tertiary)',
                                                                '&:hover': { color: 'var(--joy-palette-danger-400)' },
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </motion.div>
                                );
                            })}
                        </Stack>
                    </motion.div>
                )}
            </Box>


            {/* ═══════ Модалка добавления ═══════ */}
            <Modal open={addOpen} onClose={() => setAddOpen(false)}>
                <ModalDialog
                    sx={{
                        maxWidth: 440, width: '100%',
                        bgcolor: 'var(--joy-palette-background-surface)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 2, sm: 2.5 },
                    }}
                >
                    <ModalClose />
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 2 }}>
                        Новая премия
                    </Typography>

                    <Stack spacing={2}>
                        {/* Сотрудник (можно несколько) */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Сотрудник
                                </Typography>
                                {addPhIds.length > 0 && (
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Выбрано: {addPhIds.length}
                                    </Typography>
                                )}
                            </Box>
                            <Select
                                multiple
                                value={addPhIds}
                                onChange={(_, v) => setAddPhIds(v || [])}
                                placeholder="Выберите фотографа(ов)"
                                size="sm"
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            zIndex: 1400,
                                            maxHeight: 280,
                                            overflow: 'auto',
                                            bgcolor: 'var(--joy-palette-background-level2)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        },
                                    },
                                }}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                        {selected.map((opt) => (
                                            <Chip key={opt.value} size="sm" sx={{
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 700, fontSize: 11,
                                                border: 'none',
                                            }}>
                                                {opt.label}
                                            </Chip>
                                        ))}
                                    </Box>
                                )}
                            >
                                {employees.map(e => (
                                    <Option
                                        key={e.id}
                                        value={e.id}
                                        label={`${e.first_name} ${e.last_name}`.trim()}
                                    >
                                        {e.first_name} {e.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Box>

                        {/* Точка + Дата */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Точка
                                </Typography>
                                <Select
                                    value={addLocId}
                                    onChange={(_, v) => setAddLocId(v || '')}
                                    placeholder="Точка"
                                    size="sm"
                                >
                                    {locations.map(l => (
                                        <Option key={l.id} value={l.id}>{l.name}</Option>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Дата
                                </Typography>
                                <Input
                                    type="date" size="sm"
                                    value={addDate}
                                    onChange={(e) => setAddDate(e.target.value)}
                                />
                            </Box>
                        </Box>

                        {/* Сумма */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Сумма
                            </Typography>
                            <Input
                                type="number" size="sm"
                                placeholder="0"
                                value={addValue}
                                onChange={(e) => setAddValue(e.target.value)}
                                endDecorator={<Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>}
                            />
                        </Box>

                        {/* Комментарий */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Комментарий <span style={{ fontWeight: 400, opacity: 0.6 }}>(необязательно)</span>
                            </Typography>
                            <Textarea
                                size="sm"
                                minRows={2}
                                maxRows={4}
                                placeholder="За что начислена премия..."
                                value={addComment}
                                onChange={(e) => setAddComment(e.target.value)}
                                sx={{ bgcolor: 'var(--joy-palette-background-level2)' }}
                            />
                        </Box>

                        {/* Чекбокс — премия как администратору */}
                        <Box
                            onClick={() => setAddIsAdmin(p => !p)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 1.5, py: 1,
                                borderRadius: 'var(--joy-radius-lg)',
                                bgcolor: addIsAdmin ? 'rgba(245,158,11,0.08)' : 'var(--joy-palette-background-level2)',
                                border: `1px solid ${addIsAdmin ? 'rgba(245,158,11,0.3)' : 'var(--joy-palette-divider)'}`,
                                cursor: 'pointer',
                                transition: 'all .15s',
                            }}
                        >
                            <AdminPanelSettingsRoundedIcon sx={{ fontSize: 18, color: addIsAdmin ? '#f59e0b' : 'var(--joy-palette-text-tertiary)' }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: addIsAdmin ? '#f59e0b' : 'var(--joy-palette-text-secondary)' }}>
                                Премия как администратору
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        {/* Кнопки */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button size="sm" variant="plain" color="neutral" onClick={() => setAddOpen(false)}>
                                Отмена
                            </Button>
                            <Button
                                size="sm"
                                loading={submitting}
                                onClick={handleAdd}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                }}
                            >
                                Добавить
                            </Button>
                        </Box>
                    </Stack>
                </ModalDialog>
            </Modal>


            {/* ═══════ Модалка подтверждения удаления ═══════ */}
            <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
                <ModalDialog
                    sx={{
                        maxWidth: 360, width: '100%',
                        bgcolor: 'var(--joy-palette-background-surface)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 2, sm: 2.5 },
                    }}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить премию?
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2.5 }}>
                        Это действие нельзя отменить. Премия будет удалена из системы.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="sm" variant="plain" color="neutral" onClick={() => setDeleteId(null)}>
                            Отмена
                        </Button>
                        <Button size="sm" color="danger" loading={deleting} onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            <UndoToast
                visible={!!undoReward}
                message={undoReward?.message || ''}
                timeout={10000}
                onUndo={handleUndoReward}
                onExpire={() => setUndoReward(null)}
            />
        </motion.div>
    );
}